import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaSearch,
  FaSpinner,
  FaUserSlash,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import { formatarDiaHora, formatarHora } from "../../services/conversaoTimer";
import "./admPatrocinadoresList.css";
import "./admMaisDetalhesNaoEntregues.css";

function nomeParticipante(participante) {
  return participante?.usuario?.nome?.trim() || "—";
}

function formatarHorarioAgenda(agenda) {
  const inicio = agenda?.dt_hora_inicio || agenda?.dtHoraInicio;
  const fim = agenda?.dt_hora_fim || agenda?.dtHoraFim;
  if (!inicio) return "—";
  const inicioFmt = formatarDiaHora(String(inicio)).substring(0, 16);
  const fimFmt = fim ? formatarHora(String(fim)).substring(0, 5) : "";
  return fimFmt ? `${inicioFmt} – ${fimFmt}` : inicioFmt;
}

const AdmMaisDetalhesNaoEntregues = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const isAdmin = usuario?.tipo === "administracao";

  const [agendas, setAgendas] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agendaNoShow, setAgendaNoShow] = useState(null);
  const [isConfirmandoNoShow, setIsConfirmandoNoShow] = useState(false);

  const carregarAgendas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.get("/reunioes-pendentes");
      if (resp.status === 200) {
        const lista = (resp.data?.agendas || []).slice().sort((a, b) => {
          const inicioA = String(a.dt_hora_inicio || a.dtHoraInicio || "");
          const inicioB = String(b.dt_hora_inicio || b.dtHoraInicio || "");
          return inicioA.localeCompare(inicioB);
        });
        setAgendas(lista);
      }
    } catch (err) {
      console.error("Erro ao carregar reuniões pendentes:", err);
      setError("Erro ao carregar reuniões não entregues.");
      setAgendas([]);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar reuniões não entregues."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarAgendas();
  }, [carregarAgendas]);

  const listaFiltrada = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return agendas;
    return agendas.filter((a) => {
      const patro = nomeParticipante(a.participantes?.[1]).toLowerCase();
      const convidado = nomeParticipante(a.participantes?.[0]).toLowerCase();
      const cargo = (a.participantes?.[0]?.usuario?.cargo || "").toLowerCase();
      const empresa = (
        a.participantes?.[0]?.usuario?.empresa || ""
      ).toLowerCase();
      const horario = formatarHorarioAgenda(a).toLowerCase();
      return (
        patro.includes(termo) ||
        convidado.includes(termo) ||
        cargo.includes(termo) ||
        empresa.includes(termo) ||
        horario.includes(termo)
      );
    });
  }, [agendas, filtro]);

  const indicadores = useMemo(() => {
    const patrocinadores = new Set(
      listaFiltrada.map((a) => nomeParticipante(a.participantes?.[1]))
    );
    return {
      total: listaFiltrada.length,
      patrocinadores: patrocinadores.size,
    };
  }, [listaFiltrada]);

  const confirmarNoShow = async () => {
    if (!agendaNoShow?.id) return;
    setIsConfirmandoNoShow(true);
    try {
      const resp = await api.post("/reunioes-noshow", {
        id_agenda: agendaNoShow.id,
      });
      if (resp.status === 200) {
        toast.success(resp.data?.mensagem || "No show registrado.");
        setAgendaNoShow(null);
        await carregarAgendas();
      }
    } catch (err) {
      console.error("Erro ao registrar no show:", err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao registrar no show."
      );
    } finally {
      setIsConfirmandoNoShow(false);
    }
  };

  return (
    <div className="apl-page apl-page--padded ane-page">
      <header className="apl-header" aria-label="Cabeçalho reuniões não entregues">
        <div className="apl-header-text">
          <h1>Reuniões não entregues</h1>
          <p>
            Liste as reuniões pendentes de entrega, registre no show ou remarque
            horários quando necessário.
          </p>
        </div>

        <div className="apl-toolbar apl-toolbar--inline">
          <div className="apl-search ane-search">
            <span className="apl-search-icon" aria-hidden>
              <FaSearch size={14} />
            </span>
            <input
              type="search"
              className="apl-search-input"
              placeholder="Buscar patrocinador, convidado, empresa…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              disabled={isLoading}
              aria-label="Buscar reuniões"
            />
          </div>
          <div className="apl-toolbar-actions">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              disabled={isConfirmandoNoShow}
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </div>
      </header>

      <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {!isLoading ? (
          <section className="apl-metrics" aria-label="Indicadores">
            <div className="apl-metric apl-metric--total">
              <span className="apl-metric-value">{indicadores.total}</span>
              <span className="apl-metric-label">
                {indicadores.total === 1
                  ? "Reunião pendente"
                  : "Reuniões pendentes"}
              </span>
            </div>
            <div className="apl-metric">
              <span className="apl-metric-value">
                {indicadores.patrocinadores}
              </span>
              <span className="apl-metric-label">
                {indicadores.patrocinadores === 1
                  ? "Patrocinador"
                  : "Patrocinadores"}
              </span>
            </div>
          </section>
        ) : null}

        {isLoading ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando reuniões pendentes…</span>
            </div>
          </div>
        ) : (
          <section className="apl-card" aria-label="Tabela de reuniões">
            <div className="apl-table-wrap">
              {listaFiltrada.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">
                    {agendas.length === 0
                      ? "Nenhuma reunião pendente"
                      : "Nenhum resultado"}
                  </p>
                  <p style={{ margin: 0 }}>
                    {agendas.length === 0
                      ? "Não há reuniões não entregues no momento."
                      : "Ajuste a busca para ver outras reuniões."}
                  </p>
                </div>
              ) : (
                <table className="apl-table">
                  <thead>
                    <tr>
                      <th scope="col">Patrocinador</th>
                      <th scope="col">Convidado</th>
                      <th scope="col">Cargo</th>
                      <th scope="col">Instituição</th>
                      <th scope="col">Horário</th>
                      {isAdmin ? (
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {listaFiltrada.map((a) => {
                      const convidado = a.participantes?.[0];
                      const patrocinador = a.participantes?.[1];
                      return (
                        <tr key={a.id}>
                          <td className="apl-cell-name">
                            {nomeParticipante(patrocinador)}
                          </td>
                          <td>{nomeParticipante(convidado)}</td>
                          <td>{convidado?.usuario?.cargo || "—"}</td>
                          <td>{convidado?.usuario?.empresa || "—"}</td>
                          <td className="ane-cell-date">
                            {formatarHorarioAgenda(a)}
                          </td>
                          {isAdmin ? (
                            <td>
                              <div className="apl-actions ane-actions">
                                <button
                                  type="button"
                                  className="apl-action-btn ane-action--danger"
                                  onClick={() => setAgendaNoShow(a)}
                                  disabled={isConfirmandoNoShow}
                                >
                                  <FaUserSlash size={12} aria-hidden />
                                  No show
                                </button>
                                <Link
                                  to={`/private/adm/remarcar/${a.id}`}
                                  className="apl-action-btn"
                                >
                                  <FaCalendarAlt size={12} aria-hidden />
                                  Remarcar
                                </Link>
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}
      </div>

      <Modal
        show={Boolean(agendaNoShow)}
        onHide={() => {
          if (!isConfirmandoNoShow) setAgendaNoShow(null);
        }}
        centered
        className="agenda-confirm-excluir-modal"
        backdrop={isConfirmandoNoShow ? "static" : true}
      >
        <Modal.Header closeButton={!isConfirmandoNoShow}>
          <Modal.Title className="agenda-confirm-excluir-title">
            <FaExclamationTriangle
              className="agenda-confirm-excluir-icon"
              aria-hidden
            />
            Confirmar no show
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Confirmar no show da reunião entre{" "}
            <strong>
              {nomeParticipante(agendaNoShow?.participantes?.[1])}
            </strong>{" "}
            e{" "}
            <strong>
              {nomeParticipante(agendaNoShow?.participantes?.[0])}
            </strong>
            ?
          </p>
          {agendaNoShow ? (
            <p style={{ marginBottom: 0, color: "#64748b" }}>
              Horário: {formatarHorarioAgenda(agendaNoShow)}
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="agenda-confirm-excluir-footer">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setAgendaNoShow(null)}
            disabled={isConfirmandoNoShow}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn agenda-confirm-excluir-btn"
            onClick={confirmarNoShow}
            disabled={isConfirmandoNoShow}
          >
            {isConfirmandoNoShow ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaUserSlash size={14} aria-hidden />
            )}
            {isConfirmandoNoShow ? "Confirmando…" : "Confirmar no show"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdmMaisDetalhesNaoEntregues;
